import {DatetimeHelper} from "./DatetimeHelper";

export const PARTIAL_GOALS_INPUT_MODE_CUMULATIVE = "CUMULATIVE";
export const PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE = "DAILY_RANGE";

export const DAILY_RANGE_ENTRY_MODE_DAILY = "DAILY";
export const DAILY_RANGE_ENTRY_MODE_RANGE = "RANGE";

export class BudgetHelper {
    static deriveInputModeFromDefinition(inputDefinition) {
        const has = inputDefinition && Array.isArray(inputDefinition.entries) && inputDefinition.entries.length > 0;
        return has ? PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE : PARTIAL_GOALS_INPUT_MODE_CUMULATIVE;
    }

    static BudgetPartialGoalTranslateToSubmit(budgetPartialGoals) {
        const dic = [];
        budgetPartialGoals.forEach(bpd => {
            dic.push({
                'date': bpd.date ? DatetimeHelper.formatDateToYYYYMMDD(bpd.date) : null,
                'value': bpd.value,
                'type': bpd.type
            });
        })
        return dic;
    }

    static BudgetPartialGoalDailyRangeTranslateToSubmit(entries) {
        if (!entries || entries.length === 0)
            return { entries: [] };
        return {
            entries: entries.map(e => {
                const base = { mode: e.mode, amount: e.amount };
                if (e.mode === DAILY_RANGE_ENTRY_MODE_DAILY) {
                    base.date = e.date ? DatetimeHelper.formatDateToYYYYMMDD(e.date) : null;
                } else {
                    base.date_from = e.date_from ? DatetimeHelper.formatDateToYYYYMMDD(e.date_from) : null;
                    base.date_to = e.date_to ? DatetimeHelper.formatDateToYYYYMMDD(e.date_to) : null;
                }
                return base;
            })
        };
    }

    /**
     * Hidrata las entries que vienen del backend (con fechas YYYYMMDD strings) a Date objects
     * para el state del form.
     */
    static hydrateDailyRangeEntries(inputDefinition) {
        if (!inputDefinition || !Array.isArray(inputDefinition.entries))
            return [];
        const parse = (s) => {
            if (!s) return '';
            if (s instanceof Date) return s;
            const year = parseInt(s.slice(0, 4), 10);
            const month = parseInt(s.slice(4, 6), 10) - 1;
            const day = parseInt(s.slice(6, 8), 10);
            return new Date(year, month, day);
        };
        return inputDefinition.entries.map(e => ({
            mode: e.mode,
            date: e.mode === DAILY_RANGE_ENTRY_MODE_DAILY ? parse(e.date) : '',
            date_from: e.mode === DAILY_RANGE_ENTRY_MODE_RANGE ? parse(e.date_from) : '',
            date_to: e.mode === DAILY_RANGE_ENTRY_MODE_RANGE ? parse(e.date_to) : '',
            amount: e.amount,
        }));
    }

    /**
     * Expande las entries (con potenciales rangos) a un map { isoDay -> amount }.
     * Acepta tanto Date objects como strings YYYY-MM-DD/YYYYMMDD.
     */
    static expandDailyRangeEntries(entries) {
        const map = new Map();
        if (!entries) return map;
        for (const entry of entries) {
            const amount = parseFloat(entry.amount);
            if (!entry.amount || isNaN(amount) || amount <= 0) continue;
            if (entry.mode === DAILY_RANGE_ENTRY_MODE_DAILY) {
                if (!entry.date) continue;
                const key = BudgetHelper._dateKey(entry.date);
                if (key) map.set(key, amount);
            } else {
                if (!entry.date_from || !entry.date_to) continue;
                const from = BudgetHelper._parseLocalDate(entry.date_from);
                const to = BudgetHelper._parseLocalDate(entry.date_to);
                if (!from || !to || from > to) continue;
                let cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
                const limit = new Date(to.getFullYear(), to.getMonth(), to.getDate());
                while (cursor <= limit) {
                    const key = BudgetHelper._dateKey(cursor);
                    if (key) map.set(key, amount);
                    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
                }
            }
        }
        return map;
    }

    static _dateKey(d) {
        const date = BudgetHelper._parseLocalDate(d);
        if (!date) return null;
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    /**
     * Parser robusto que trata las fechas como **calendario local** (no UTC).
     * `new Date("2026-05-01")` interpreta UTC midnight y en TZ negativa (ej: Argentina UTC-3)
     * cae al día anterior — bug que rompe el período. Acá parseamos manualmente cuando llega
     * un string YYYY-MM-DD o YYYYMMDD.
     */
    static _parseLocalDate(val) {
        if (!val) return null;
        if (val instanceof Date) {
            return isNaN(val) ? null : new Date(val.getFullYear(), val.getMonth(), val.getDate());
        }
        if (typeof val === 'string') {
            const m = val.match(/^(\d{4})-?(\d{2})-?(\d{2})/);
            if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
        }
        const fallback = new Date(val);
        return isNaN(fallback) ? null : new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
    }

    /**
     * Calcula el feedback inline para mostrar al usuario en modo DAILY_RANGE:
     * - spikeTotal: cuánto consumen los spike days en total
     * - nonSpikeDays: cuántos días no-spike quedan
     * - dailyBaseline: cuánto promedio se invertiría cada día no-spike
     * - hasOverlaps: si hay días duplicados entre entries
     */
    static computeDailyRangeFeedback(totalBudget, periodFromDate, periodToDate, entries) {
        const expanded = BudgetHelper.expandDailyRangeEntries(entries);
        const periodDays = BudgetHelper._daysBetweenInclusive(periodFromDate, periodToDate);

        // Detectar overlaps comparando count de keys vs cantidad teórica de días en entries
        let theoreticalCount = 0;
        for (const entry of entries || []) {
            if (!entry.amount) continue;
            if (entry.mode === DAILY_RANGE_ENTRY_MODE_DAILY && entry.date) theoreticalCount += 1;
            else if (entry.mode === DAILY_RANGE_ENTRY_MODE_RANGE && entry.date_from && entry.date_to) {
                const f = entry.date_from instanceof Date ? entry.date_from : new Date(entry.date_from);
                const t = entry.date_to instanceof Date ? entry.date_to : new Date(entry.date_to);
                if (!isNaN(f) && !isNaN(t) && f <= t) {
                    theoreticalCount += BudgetHelper._daysBetweenInclusive(f, t);
                }
            }
        }
        const hasOverlaps = theoreticalCount > expanded.size;

        const spikeTotal = Array.from(expanded.values()).reduce((acc, v) => acc + v, 0);
        const nonSpikeDays = Math.max(0, periodDays - expanded.size);
        const remainder = Math.max(0, parseFloat(totalBudget || 0) - spikeTotal);
        const dailyBaseline = nonSpikeDays > 0 ? remainder / nonSpikeDays : 0;
        const exceedsBudget = spikeTotal > parseFloat(totalBudget || 0);

        return { spikeTotal, nonSpikeDays, dailyBaseline, hasOverlaps, exceedsBudget, periodDays };
    }

    static _daysBetweenInclusive(from, to) {
        const f = BudgetHelper._parseLocalDate(from);
        const t = BudgetHelper._parseLocalDate(to);
        if (!f || !t || f > t) return 0;
        return Math.round((t - f) / 86_400_000) + 1;
    }

    /**
     * Convierte un input DAILY_RANGE a la lista de cumulative checkpoints equivalentes
     * (mismo shape que el payload partial_goals tradicional).
     *
     * Para cada spike day D dentro del período generamos un checkpoint en D y otro en D-1
     * (si D-1 también está en el período y no es spike). El pacing engine del backend
     * interpola lineal entre cumulatives consecutivos, así el daily target del día D = amount
     * declarado para ese spike. El último día del período se omite: el pacing engine usa el
     * total como ancla final implícita cuando no hay current_budget_partial_goal.
     *
     * Output: [{date: "YYYYMMDD", type: "ALLOCATED_AMOUNT_GOAL", value: int}, ...]
     */
    static deriveCumulativeFromDailyRange(totalBudget, periodFromDate, periodToDate, entries) {
        if (!entries || entries.length === 0) return [];

        const total = parseFloat(totalBudget || 0);
        const start = BudgetHelper._parseLocalDate(periodFromDate);
        const end = BudgetHelper._parseLocalDate(periodToDate);
        if (!start || !end) return [];

        const expanded = BudgetHelper.expandDailyRangeEntries(entries);
        if (expanded.size === 0) return [];

        const periodDays = BudgetHelper._daysBetweenInclusive(start, end);
        let spikeTotal = 0;
        for (const v of expanded.values()) spikeTotal += v;
        const nonSpikeDays = periodDays - expanded.size;
        const baseline = nonSpikeDays > 0 ? Math.max(0, total - spikeTotal) / nonSpikeDays : 0;

        const cumulativeByKey = new Map();
        let cumulative = 0;
        const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const limit = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        while (cursor <= limit) {
            const key = BudgetHelper._dateKey(cursor);
            cumulative += expanded.has(key) ? expanded.get(key) : baseline;
            cumulativeByKey.set(key, cumulative);
            cursor.setDate(cursor.getDate() + 1);
        }

        const checkpointKeys = new Set();
        const endKey = BudgetHelper._dateKey(end);
        for (const spikeKey of expanded.keys()) {
            if (spikeKey !== endKey) checkpointKeys.add(spikeKey);
            const spikeDate = BudgetHelper._dateFromKey(spikeKey);
            spikeDate.setDate(spikeDate.getDate() - 1);
            const prevKey = BudgetHelper._dateKey(spikeDate);
            if (prevKey >= BudgetHelper._dateKey(start)
                && prevKey !== endKey
                && !expanded.has(prevKey)) {
                checkpointKeys.add(prevKey);
            }
        }

        const sorted = Array.from(checkpointKeys).sort();
        return sorted.map(k => ({
            date: k.replaceAll('-', ''),
            type: 'ALLOCATED_AMOUNT_GOAL',
            value: Math.round(cumulativeByKey.get(k)),
        }));
    }

    static _dateFromKey(key) {
        const [y, m, d] = key.split('-').map(s => parseInt(s, 10));
        return new Date(y, m - 1, d);
    }
}